import { useMutation } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import { AxiosError } from 'axios';

// common
import useGeneralHook from 'common/hook/useGeneralHook';
import { ROUTES_PATH } from 'layout/constants';
import useService from 'common/hook/useService';
import { handleException } from 'modules/account/detail/helpers/utils';

// screen
import type {
  AccountInfo,
  ReplaceCreateUserData,
  RolesAssignment,
  RolesToAssign,
  UserInfo,
} from 'modules/account/types';

export const defaultAccountInfo: AccountInfo = {
  username: '',
  email: '',
  password: '',
  passwordRetype: '',
  expiredAt: '',
  status: true,
};

export const defaultUserInfo: UserInfo = {
  name: '',
  organizationTypeId: '',
  unitId: '',
  jobTitleId: '',
  jobPosition: '',
  phone: '',
  address: '',
  cccd: '',
  issuePlace: '',
  issueDate: '',
  dob: '',
  genderId: '',
};

export const defaultRolesAssignment: RolesAssignment = {
  forClientBusiness: [],
  forGovAgencyBusiness: [],
  forAdminRoles: [],
  forSystemRoles: [],
};

export const defaultRolesToAssign: RolesToAssign = {
  customersSource: [],
  allRoles: {
    adminRoles: [],
    allClients: [],
    clientRoles: [],
    clientsForAdminRoles: [],
    clientsForClientRoles: [],
    govAgencyRoles: [],
    systemRoles: [],
    rootSystemRoles: [],
  },
  rolesAssignment: defaultRolesAssignment,
};

// export interface CreateUserData extends z.infer<typeof schemas.CreateUserData> {
//   org: typeof schemas.CreateUserData._type.org;
//   userData: typeof schemas.CreateUserData._type.userData;
//   roleAssignment: typeof schemas.CreateUserData._type.roleAssignment;
// }
export type CreateUserData = ReplaceCreateUserData;

interface MutatingProps {
  data: CreateUserData;
}

interface UseCreateAccountProps {
  onSuccess?: () => void;
}

const useCreateAccount = (props?: UseCreateAccountProps) => {
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useGeneralHook();
  const service = useService();
  const accountsPage = ['', ROUTES_PATH.ad.index, ROUTES_PATH.account.index];

  const mutation = useMutation({
    mutationFn: async ({ data }: MutatingProps) => {
      try {
        if (!service) return null;

        const payload = {
          ...data,
          roleAssignment: {
            forClientBusiness: data.roleAssignment.forClientBusiness.map((r) => ({
              roleId: r.roleId,
              orgId: r.orgId,
            })),
            forGovAgencyBusiness: data.roleAssignment.forGovAgencyBusiness.map((item) => ({
              roleId: item.roleId,
              orgId: item.orgId,
              includedClientByBoundary: item.includedClientByBoundary,
              includedClientIds: item.includedClientIds ? [...item.includedClientIds] : null,
            })),
            forAdmin: data.roleAssignment.forAdmin.map((r) => ({
              roleId: r.roleId,
              orgId: r.orgId,
            })),
            forSystem: data.roleAssignment.forSystem.map((r) => ({
              roleId: r.roleId,
              orgId: r.orgId,
            })),
          },
        };

        const result = await service.UserManagementController_createUser(payload);
        if (result.userData) {
          enqueueSnackbar({
            message: formatMessage({ id: 'createSuccess' }),
            variant: 'success',
          });

          // Call the success callback if provided
          props?.onSuccess?.();

          navigate(accountsPage.join('/'));
        }
      } catch (error) {
        const errorStatus = handleException(error, { notifyUser: false });
        if (errorStatus === 400) {
          enqueueSnackbar({
            message: formatMessage({
              // @ts-expect-error: error is not typed
              id: `error.${(error as AxiosError)?.response?.data?.code || 'error'}`,
            }),
            variant: 'error',
          });
        }
        throw error;
      }
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
};

export default useCreateAccount;
